import { useCallback, useEffect, useRef } from "react";

import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useRecombee } from "@/hooks/useRecombee";
import { getOrCreateSession, updateSessionMessageCount } from "@/utils/recombeeSessionStorage";

const MS_PER_SECOND = 1000;

export interface IUseChatSessionTrackingProps {
  conversationId: string | null;
  characterId: string;
  isPublicBot: boolean;
}

export interface IUseChatSessionTrackingReturn {
  sendViewPortionEvent: (overrideConversationId?: string) => void;
}

export function useChatSessionTracking({ conversationId, characterId, isPublicBot }: IUseChatSessionTrackingProps): IUseChatSessionTrackingReturn {
  const { sendUserInteractionViewPortion, recombeeUserId } = useRecombee();
  const { hasFeatureFlag: hasViewPortionTracking } = useFeatureFlag(FEATURE_FLAGS.RECOMBEE_VIEW_PORTIONS);

  const shouldTrack = hasViewPortionTracking && isPublicBot;

  const refSession = useRef<ReturnType<typeof getOrCreateSession> | null>(null);
  const refConversationId = useRef<string | null>(conversationId);

  // Keep ref in sync with prop so callback always has latest value
  useEffect(() => {
    refConversationId.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    if (!shouldTrack || !conversationId) {
      refSession.current = null;
      return;
    }
    refSession.current = getOrCreateSession(characterId, conversationId);
    return () => {
      // Only clear in-memory ref on unmount/dep change — do NOT call clearSession here.
      // sessionStorage must persist across SPA navigation so messageCount and startTime
      // are preserved when the user navigates back to the same conversation.
      // A new session is created by getOrCreateSession when conversationId changes.
      refSession.current = null;
    };
  }, [characterId, conversationId, shouldTrack]);

  const sendViewPortionEvent = useCallback(
    (overrideConversationId?: string) => {
      const currentConversationId = overrideConversationId ?? refConversationId.current;
      if (!shouldTrack || !currentConversationId) return;

      // Lazily initialize session if not yet set (handles race condition on first message)
      if (!refSession.current) {
        refSession.current = getOrCreateSession(characterId, currentConversationId);
      }

      const updatedSession = updateSessionMessageCount(characterId, currentConversationId);

      refSession.current = updatedSession;

      const timeSpent = Math.floor((Date.now() - updatedSession.startTime) / MS_PER_SECOND);

      void sendUserInteractionViewPortion(recombeeUserId, characterId, 1, {
        sessionId: updatedSession.sessionId,
        timeSpent,
        additionalData: {
          messageCount: updatedSession.messageCount,
        },
        ...(updatedSession.recommId && { recommId: updatedSession.recommId }),
      });
    },
    [shouldTrack, characterId, recombeeUserId, sendUserInteractionViewPortion],
  );

  return {
    sendViewPortionEvent,
  };
}
